import { CoverageTag } from '@/features/drugs/types/coverage-tag';
import { artiaProtectedApi } from '@/lib/api-client';
import { deleteWithValidation, postWithValidation, putWithValidation, RequestOptions } from '@/lib/api-helpers';
import { Drug } from '@/products';

import { ProductFormValue } from '../product-form.provider';

type ProductRequest = {
  name: string,
  displayAsBrand: boolean,
  genericName?: string,
  clientId: number
  classificationId?: number;
  newClassification?: string;
};

export const useProductsService = () => {
  const getProduct = (id: number): Promise<Drug> => {
    return artiaProtectedApi.get(`drugs/${id}`);
  };

  const createProduct = (form: ProductFormValue): Promise<any> => {
    const data: ProductRequest = {
      name: form.name,
      displayAsBrand: form.displayAsBrand,
      genericName: form.genericName,
      clientId: form.client?.id
    };

    if (form.classification) {
      if (form.classification.value) {
        data.classificationId = form.classification.value;
      } else {
        data.newClassification = form.classification.name;
      }
    }

    const options: RequestOptions<any> = {
      data
    };

    return postWithValidation('drugs', options);
  };

  const updateProduct = (id: number, form: ProductFormValue): Promise<any> => {
    const data: ProductRequest = {
      name: form.name,
      displayAsBrand: form.displayAsBrand,
      genericName: form.genericName,
      clientId: form.client?.id
    };

    if (form.classification) {
      if (form.classification.value) {
        data.classificationId = form.classification.value;
      } else {
        data.newClassification = form.classification.name;
      }
    }

    const options: RequestOptions<any> = {
      data
    };

    return putWithValidation(`drugs/${id}`, options);
  };

  const uploadDrugPackagingsFile = (file: File, productId: number): Promise<any> => {
    const data = new FormData();

    data.append('Upload', file);

    const options: RequestOptions<any> = {
      data
    };

    return postWithValidation(`drugs/${productId}/import-drug-packagings`, options);
  };

  const createCoverageTag = (drugId: number, data: Omit<CoverageTag, 'id'>) => {
    return postWithValidation(`drugs/${drugId}/coverage-tags`, { data });
  };

  const updateCoverageTag = (drugId: number, coverageTag: CoverageTag) => {
    return putWithValidation(`drugs/${drugId}/coverage-tags/${coverageTag.id}`, {
      data: coverageTag
    });
  };

  const deleteCoverageTag = (drugId: number, coverageTagId: number) => {
    return deleteWithValidation(`drugs/${drugId}/coverage-tags/${coverageTagId}`);
  };

  return {
    getProduct,
    createProduct,
    updateProduct,
    uploadDrugPackagingsFile,
    createCoverageTag,
    updateCoverageTag,
    deleteCoverageTag,
  };
};
